import { CalculationFormula } from '@/types/calculation-formulas';

export const HENRY_OXFORD_FORMULA_ID = 'henry_oxford';
export const HENRY_OXFORD_FALLBACK_NAME = 'Henry Oxford';

const normalizeFormulaToken = (value: string | undefined | null): string =>
  String(value ?? '').trim().toLowerCase();

export const getFormulaDisplayName = (formula: CalculationFormula | undefined): string =>
  formula?.displayName || formula?.label || formula?.formulaName || '';

export const isHenryOxfordFormula = (formula: CalculationFormula | undefined): boolean => {
  if (!formula) return false;

  const normalizedId = normalizeFormulaToken(formula.id);
  const normalizedName = normalizeFormulaToken(
    `${formula.displayName ?? ''} ${formula.label ?? ''} ${formula.formulaName ?? ''} ${formula.name ?? ''}`
  );

  return normalizedId === HENRY_OXFORD_FORMULA_ID || normalizedName.includes('henry oxford');
};

export const findHenryOxfordFormula = (formulas: CalculationFormula[]): CalculationFormula | undefined =>
  formulas.find(isHenryOxfordFormula);

export const getPreferredFormulaFallback = (
  viableFormulas: CalculationFormula[],
  allFormulas: CalculationFormula[] = []
): CalculationFormula | undefined =>
  findHenryOxfordFormula(viableFormulas) ??
  findHenryOxfordFormula(allFormulas) ??
  viableFormulas[0] ??
  allFormulas[0];

export const getFormulaFallbackDisplayName = (formula: CalculationFormula | undefined): string => {
  if (isHenryOxfordFormula(formula)) {
    return HENRY_OXFORD_FALLBACK_NAME;
  }

  return getFormulaDisplayName(formula) || 'Recommended Formula';
};

export const shouldPreferHenryOxfordForAge = (ageInMonths: number | null): boolean =>
  ageInMonths !== null && ageInMonths >= 12 && ageInMonths < 19 * 12;

export const isIomFormulaSelection = (
  formulaId: string | undefined | null,
  formulaName?: string | null
): boolean => {
  const normalizedId = normalizeFormulaToken(formulaId);
  const normalizedName = normalizeFormulaToken(formulaName);

  if (!normalizedId && !normalizedName) {
    return false;
  }

  const hasIomId =
    normalizedId === 'iom' ||
    normalizedId === 'iom_usa' ||
    normalizedId === 'iom_us' ||
    normalizedId === 'iom_2005' ||
    normalizedId === 'iom2005' ||
    /(^|[_-])iom([_-]|$)/.test(normalizedId);

  if (hasIomId) {
    return true;
  }

  return /\biom\b/.test(normalizedName) || normalizedName.includes('institute of medicine');
};
